import React from 'react';
import { GraduationCap, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';

const Team = () => {
  const { t } = useTranslation();

  return (
    <section id="team" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('team.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('team.subtitle')}
          </p>
        </div>

        {/* Coordinator */}
        <div className="max-w-5xl mx-auto mb-16">
          <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-br from-pink-50 to-purple-50">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="relative h-96 md:h-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/ch5ijq5y_1000089484.jpg"
                    alt={t('team.coordinatorName')}
                    className="w-full h-full object-cover"
                  />
                  <div className="hidden md:block absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2">
                    <p className="font-semibold text-gray-900">{t('team.coordinatorName')}</p>
                    <p className="text-sm text-pink-600">{t('team.coordinatorRole')}</p>
                  </div>
                </div>
                
                <div className="p-8 space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {t('team.coordinatorName')}
                    </h3>
                    <p className="text-pink-600 font-semibold mb-4">
                      {t('team.coordinatorRole')}
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      {t('team.coordinatorBio')}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <GraduationCap className="w-5 h-5 text-pink-600" />
                      <h4 className="font-semibold text-gray-900">{t('team.qualificationsTitle')}</h4>
                    </div>
                    <ul className="space-y-2">
                      <li className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-pink-600 mt-1">•</span>
                        <span>{t('team.qualification1')}</span>
                      </li>
                      <li className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-pink-600 mt-1">•</span>
                        <span>{t('team.qualification2')}</span>
                      </li>
                      <li className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-pink-600 mt-1">•</span>
                        <span>{t('team.qualification3')}</span>
                      </li>
                      <li className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-pink-600 mt-1">•</span>
                        <span>{t('team.qualification4')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Educators */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow overflow-hidden group">
            <CardContent className="p-0">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/f6s4mmbs_1000087876.jpg"
                  alt={t('team.educator1Name')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-1">{t('team.educator1Name')}</h3>
                  <p className="text-pink-200 font-medium mb-3 text-sm">{t('team.educator1Role')}</p>
                  <p className="text-sm text-white/90">{t('team.educator1Desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow overflow-hidden group">
            <CardContent className="p-0">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/szvel5hu_1000087875.jpg"
                  alt={t('team.educator2Name')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-1">{t('team.educator2Name')}</h3>
                  <p className="text-pink-200 font-medium mb-3 text-sm">{t('team.educator2Role')}</p>
                  <p className="text-sm text-white/90">{t('team.educator2Desc')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Common Qualifications */}
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t('team.commonQualificationsTitle')}
                </h3>
              </div>
              <ul className="grid md:grid-cols-2 gap-3">
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-purple-600 mt-1">✓</span>
                  <span>{t('team.commonQual1')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-purple-600 mt-1">✓</span>
                  <span>{t('team.commonQual2')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-purple-600 mt-1">✓</span>
                  <span>{t('team.commonQual3')}</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-purple-600 mt-1">✓</span>
                  <span>{t('team.commonQual4')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Team;
